import type { ProcessedDLOMResults } from "@/lib/api-service"

export interface ExportOptions {
  includeSummary: boolean
  includePeerGroup: boolean
  includeDiscountMatrix: boolean
  includeAnalysis: boolean
  includeParameters: boolean
}

export interface ExportData {
  summary: {
    baseDiscount: number
    adjustedDiscount: number
    finalValue: number
    originalValue: number
  }
  peerGroup: Array<{
    company: string
    marketCap: number
    revenue: number
    discount: number
    volatility: number
  }>
  matrices: Array<{
    title: string
    rows: Array<{
      label: string
      v25: number
      v50: number
      v75: number
    }>
  }>
  calculationDate: string
  parameters?: {
    valuationDate?: string
    sicCode?: string
    marketValue?: number
    revenue?: number
  }
}

export function buildExportData(results: ProcessedDLOMResults, options: ExportOptions): ExportData {
  const matrices: ExportData["matrices"] = []

  const addMatrix = (title: string, mat?: number[][]) => {
    if (!mat || !Array.isArray(mat) || mat.length !== 3 || !mat.every((r) => Array.isArray(r) && r.length === 3)) {
      return
    }
    const rowLabels = [
      "Short Liquidation Period (25th Percentile)",
      "Median Liquidation Period",
      "Long Liquidation Period (75th Percentile)",
    ]
    matrices.push({
      title,
      rows: mat.map((row, idx) => ({
        label: rowLabels[idx],
        v25: row[0] * 100,
        v50: row[1] * 100,
        v75: row[2] * 100,
      })),
    })
  }

  if (options.includeDiscountMatrix) {
    addMatrix("AAE (No Restrictions)", results.matrices.discountAASP)
    addMatrix("ME (No Restrictions)", results.matrices.discountBSP)
    addMatrix("LBE (No Restrictions)", results.matrices.discountLBP)
    addMatrix("AAE (With Restrictions)", results.matrices.discountAASPLP)
    addMatrix("ME (With Restrictions)", results.matrices.discountBSPLP)
    addMatrix("LBE (With Restrictions)", results.matrices.discountLBPLP)

    // Include user-input matrices only when they look valid (3x3 with 0..1 proportions)
    const matsUI = results.matricesUI
    const looksValid = (m: number[][] | undefined) =>
      !!m && m.length === 3 && m.every((r) => r.length === 3 && r.every((v) => Number.isFinite(v) && v >= 0 && v <= 1))
    if (looksValid(matsUI.discountAASPUI)) addMatrix("AAE (User Input)", matsUI.discountAASPUI)
    if (looksValid(matsUI.discountBSPUI)) addMatrix("ME (User Input)", matsUI.discountBSPUI)
    if (looksValid(matsUI.discountLBPUI)) addMatrix("LBE (User Input)", matsUI.discountLBPUI)
    if (looksValid(matsUI.discountAASPLPUI)) addMatrix("AAE (User Input, With Restrictions)", matsUI.discountAASPLPUI)
    if (looksValid(matsUI.discountBSPLPUI)) addMatrix("ME (User Input, With Restrictions)", matsUI.discountBSPLPUI)
    if (looksValid(matsUI.discountLBPLPUI)) addMatrix("LBE (User Input, With Restrictions)", matsUI.discountLBPLPUI)
  }

  const peerGroup = options.includePeerGroup
    ? [
        { company: "25th Percentile", marketCap: 0, revenue: 0, discount: 0, volatility: results.statistics.volatility.q1 * 100 },
        { company: "Median", marketCap: 0, revenue: 0, discount: 0, volatility: results.statistics.volatility.median * 100 },
        { company: "75th Percentile", marketCap: 0, revenue: 0, discount: 0, volatility: results.statistics.volatility.q3 * 100 },
      ]
    : []

  return {
    summary: results.summary,
    peerGroup,
    matrices,
    calculationDate: new Date().toLocaleString(),
    parameters: options.includeParameters
      ? {
          marketValue: results.summary.originalValue,
        }
      : undefined,
  }
}

export function exportToCSV(data: ExportData): void {
  const csvContent = generateCSVContent(data)
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
  const link = document.createElement("a")

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob)
    link.setAttribute("href", url)
    link.setAttribute("download", `DLOM_Report_${formatDateForFilename(data.calculationDate)}.csv`)
    link.style.visibility = "hidden"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

// Deprecated placeholder kept for compatibility if referenced elsewhere
export async function exportToPDF(): Promise<void> {}

export function printReportWindow(results: ProcessedDLOMResults, options: ExportOptions): void {
  const data = buildExportData(results, options)

  const htmlParts: string[] = []
  const push = (s: string) => htmlParts.push(s)

  const escapeHtml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")

  push("<!doctype html><html><head><meta charset=\"utf-8\">")
  push(`<title>DLOM_Report_${formatDateForFilename(data.calculationDate)}</title>`)
  push(`<style>
  @page { size: letter; margin: 16mm; }
  body { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, \"Apple Color Emoji\", \"Segoe UI Emoji\"; color: #000; background: #fff; font-size: 12px; line-height: 1.45; }
  h1 { font-size: 20px; margin: 0 0 8px; }
  h2 { font-size: 16px; margin: 16px 0 8px; }
  h3 { font-size: 14px; margin: 12px 0 6px; }
  table { width: 100%; border-collapse: collapse; margin: 8px 0 12px; }
  th, td { border: 1px solid #000; padding: 6px; }
  th { text-align: left; background: #f3f3f3; }
  .section { page-break-inside: avoid; }
  .page-break { page-break-before: always; }
  .muted { color: #555; }
  .kv { width: 100%; border-collapse: collapse; }
  .kv td { border: 1px solid #000; padding: 6px; }
  .kv td:first-child { width: 50%; font-weight: 600; }
  </style>`)
  push("</head><body>")

  push(`<h1>DLOM Calculation Report</h1>`) 
  push(`<div class=\"muted\">Generated on: ${escapeHtml(data.calculationDate)}</div>`) 

  if (options.includeSummary && data.summary) {
    push(`<h2>Executive Summary</h2>`) 
    push(`<table class=\"kv\"><tbody>
      <tr><td>Base Discount</td><td>${formatPercent(data.summary.baseDiscount)}</td></tr>
      <tr><td>Adjusted Discount</td><td>${formatPercent(data.summary.adjustedDiscount)}</td></tr>
      <tr><td>Original Value</td><td>${formatCurrency(data.summary.originalValue)}</td></tr>
      <tr><td>Final Value</td><td>${formatCurrency(data.summary.finalValue)}</td></tr>
      <tr><td>Discount Amount</td><td>${formatCurrency(data.summary.originalValue - data.summary.finalValue)}</td></tr>
    </tbody></table>`) 
  }

  if (options.includeParameters && data.parameters) {
    push(`<h2>Calculation Parameters</h2>`) 
    push(`<table class=\"kv\"><tbody>`) 
    if (data.parameters.valuationDate) push(`<tr><td>Valuation Date</td><td>${escapeHtml(data.parameters.valuationDate)}</td></tr>`)
    if (data.parameters.sicCode) push(`<tr><td>SIC Code</td><td>${escapeHtml(data.parameters.sicCode)}</td></tr>`)
    if (typeof data.parameters.marketValue === "number") push(`<tr><td>Market Value</td><td>${formatCurrency(data.parameters.marketValue)}</td></tr>`)
    if (typeof data.parameters.revenue === "number") push(`<tr><td>Revenue</td><td>${formatCurrency(data.parameters.revenue)}</td></tr>`)
    push(`</tbody></table>`) 
  }

  if (options.includePeerGroup && data.peerGroup && data.peerGroup.length > 0) {
    push(`<h2>Peer Group Overview</h2>`) 
    push(`<table><thead><tr>
      <th>Label</th><th>Volatility</th>
    </tr></thead><tbody>`) 
    data.peerGroup.forEach((p) => {
      push(`<tr><td>${escapeHtml(p.company)}</td><td style=\"text-align:right\">${formatPercent(p.volatility)}</td></tr>`) 
    })
    push(`</tbody></table>`) 
  }

  if (options.includeDiscountMatrix && data.matrices && data.matrices.length > 0) {
    push(`<h2>Discount Matrices</h2>`) 
    data.matrices.forEach((m, i) => {
      if (i > 0 && i % 2 === 0) push(`<div class=\"page-break\"></div>`) 
      push(`<h3>${escapeHtml(m.title)}</h3>`) 
      push(`<table><thead><tr>
        <th>Row</th>
        <th style=\"text-align:right\">Low Volatility (25th Percentile)</th>
        <th style=\"text-align:right\">Median Volatility</th>
        <th style=\"text-align:right\">High Volatility (75th Percentile)</th>
      </tr></thead><tbody>`) 
      m.rows.forEach((r) => {
        push(`<tr>
          <td>${escapeHtml(r.label)}</td>
          <td style=\"text-align:right\">${formatPercent(r.v25)}</td>
          <td style=\"text-align:right\">${formatPercent(r.v50)}</td>
          <td style=\"text-align:right\">${formatPercent(r.v75)}</td>
        </tr>`) 
      })
      push(`</tbody></table>`) 
    })
  }

  if (options.includeAnalysis) {
    push(`<h2>Key Insights & Analysis</h2><div class=\"muted\">Analysis generated by the application.</div>`) 
  }

  push("</body></html>")

  const html = htmlParts.join("")
  const win = window.open("", "_blank")
  if (!win) return
  win.document.open()
  win.document.write(html)
  win.document.close()
  const trigger = () => { try { win.focus(); win.print(); } catch {} }
  const docAny = win.document as unknown as { fonts?: { ready?: Promise<void> } }
  if (docAny.fonts && docAny.fonts.ready) {
    docAny.fonts.ready.then(() => setTimeout(trigger, 50))
  } else {
    setTimeout(trigger, 150)
  }
}

function generateCSVContent(data: ExportData): string {
  const lines: string[] = []
  const push = (...cols: (string | number)[]) => {
    lines.push(cols.map(csvEscape).join(","))
  }

  push("DLOM Calculation Export")
  push(`Generated on: ${data.calculationDate}`)
  lines.push("")

  if (data.summary) {
    push("SUMMARY")
    push("Metric", "Value")
    push("Base Discount", formatPercent(data.summary.baseDiscount))
    push("Adjusted Discount", formatPercent(data.summary.adjustedDiscount))
    push("Original Value", formatCurrency(data.summary.originalValue))
    push("Final Value", formatCurrency(data.summary.finalValue))
    push("Discount Amount", formatCurrency(data.summary.originalValue - data.summary.finalValue))
    lines.push("")
  }

  if (data.parameters) {
    push("CALCULATION PARAMETERS")
    push("Parameter", "Value")
    if (data.parameters.valuationDate) push("Valuation Date", data.parameters.valuationDate)
    if (data.parameters.sicCode) push("SIC Code", data.parameters.sicCode)
    if (typeof data.parameters.marketValue === "number") push("Market Value", formatCurrency(data.parameters.marketValue))
    if (typeof data.parameters.revenue === "number") push("Revenue", formatCurrency(data.parameters.revenue))
    lines.push("")
  }

  if (data.peerGroup && data.peerGroup.length > 0) {
    push("PEER GROUP ANALYSIS")
    push("Label", "Volatility")
    data.peerGroup.forEach((peer) => {
      push(peer.company, formatPercent(peer.volatility))
    })
    lines.push("")
  }

  if (data.matrices && data.matrices.length > 0) {
    data.matrices.forEach((m) => {
      push("DISCOUNT MATRIX")
      push(m.title)
      push("Row", "Low Volatility (25th Percentile)", "Median Volatility", "High Volatility (75th Percentile)")
      m.rows.forEach((r) => {
        push(r.label, formatPercent(r.v25), formatPercent(r.v50), formatPercent(r.v75))
      })
      lines.push("")
    })
  }

  return lines.join("\r\n")
}

// Legacy text generator retained only for potential debugging usage
// removed legacy generator

function formatDateForFilename(dateString: string): string {
  return dateString.replace(/[/:]/g, "-").replace(/\s/g, "_")
}

function csvEscape(value: string | number): string {
  const s = String(value)
  if (/[",\n\r]/.test(s)) {
    return '"' + s.replace(/"/g, '""') + '"'
  }
  return s
}

function formatPercent(value: number): string {
  return `${Number(value).toFixed(2)}%`
}

function formatCurrency(value: number): string {
  return `$${Number(value).toLocaleString()}`
}
